import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {RuleListItem} from './ruleListHelpers';

/*
 * Types.
 */

export enum RuleListDialogsStatesEnum {
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  DELETE = 'DELETE',
}

/*
 * Constants.
 */

const ruleListDialogBodies: Record<RuleListDialogsStatesEnum, ReactNode> = {
  [RuleListDialogsStatesEnum.ACTIVATE]: (
    <Trans id="rule-list-activate-dialog-body">
      Activating rules can impact several conversations moving forward. If something is working incorrectly,
      you can deactivate them later.
    </Trans>
  ),
  [RuleListDialogsStatesEnum.DEACTIVATE]: (
    <Trans id="rule-list-deactivate-dialog-body">
      Deactivating rules can impact several conversations moving forward. If something is working incorrectly,
      you can reactivate them later.
    </Trans>
  ),
  [RuleListDialogsStatesEnum.DELETE]: (
    <Trans id="rule-list-delete-dialog-body">
      Are you sure you want to permanently delete? Cannot be undone.
    </Trans>
  ),
};

const ruleListDialogOkButtons: Record<RuleListDialogsStatesEnum, ReactNode> = {
  [RuleListDialogsStatesEnum.ACTIVATE]: <Trans id="rule-list-activate-dialog-ok">Activate</Trans>,
  [RuleListDialogsStatesEnum.DEACTIVATE]: <Trans id="rule-list-deactivate-dialog-ok">Deactivate</Trans>,
  [RuleListDialogsStatesEnum.DELETE]: <Trans id="rule-list-delete-dialog-ok">Delete forever</Trans>,
};

/*
 * Props.
 */

interface RuleListDialogsProps {
  dialogsState: RuleListDialogsStatesEnum | undefined;
  targetItems: ReadonlyArray<RuleListItem>;
  onClose: Handler;

  onSetActive: Handler;
  onSetInactive: Handler;
  onDelete: Handler;
}

/*
 * Component.
 */

export const RuleListDialogs: FC<RuleListDialogsProps> = (props) => {
  const {dialogsState, onClose, targetItems, onSetActive, onSetInactive, onDelete} = props;

  if (!dialogsState) {
    return null;
  }

  return (
    <OkCancelDialog
      title={renderTitle(dialogsState, targetItems)}
      okButtonLabel={ruleListDialogOkButtons[dialogsState]}
      okButtonVariant={
        dialogsState === RuleListDialogsStatesEnum.DELETE
          ? ButtonVariantsEnum.DANGER
          : ButtonVariantsEnum.PRIMARY
      }
      onRequestClose={onClose}
      onOkClick={() => {
        switch (dialogsState) {
          case RuleListDialogsStatesEnum.ACTIVATE:
            onSetActive();
            break;

          case RuleListDialogsStatesEnum.DEACTIVATE:
            onSetInactive();
            break;

          case RuleListDialogsStatesEnum.DELETE:
            onDelete();
            break;

          default:
            throw new UnreachableError(dialogsState);
        }
        onClose();
      }}
    >
      {ruleListDialogBodies[dialogsState]}
    </OkCancelDialog>
  );
};

/*
 * Helpers.
 */

function renderTitle(state: RuleListDialogsStatesEnum, targetItems: ReadonlyArray<RuleListItem>) {
  if (targetItems.length > 1) {
    switch (state) {
      case RuleListDialogsStatesEnum.ACTIVATE:
        return <Trans id="rule-list-activate-title-many">Activate {targetItems.length} rules</Trans>;

      case RuleListDialogsStatesEnum.DEACTIVATE:
        return <Trans id="rule-list-deactivate-title-many">Deactivate {targetItems.length} rules</Trans>;

      case RuleListDialogsStatesEnum.DELETE:
        return <Trans id="rule-list-delete-title-many">Delete {targetItems.length} rules</Trans>;

      default:
        throw new UnreachableError(state);
    }
  }

  const [singleItem] = targetItems;
  if (!singleItem) {
    return null;
  }

  const {name} = singleItem;
  switch (state) {
    case RuleListDialogsStatesEnum.ACTIVATE:
      return <Trans id="rule-list-activate-header-single">Activate &quot;{name}&quot; rule</Trans>;

    case RuleListDialogsStatesEnum.DEACTIVATE:
      return <Trans id="rule-list-deactivate-header-single">Deactivate &quot;{name}&quot; rule</Trans>;

    case RuleListDialogsStatesEnum.DELETE:
      return <Trans id="rule-list-delete-header-single">Delete &quot;{name}&quot; rule</Trans>;

    default:
      throw new UnreachableError(state);
  }
}
